import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Locales matching SUPPORTED_LANGUAGES in useLanguage.ts
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/nl';
import 'dayjs/locale/pt';
import 'dayjs/locale/sv';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Set dayjs's global locale — called from i18n.ts on init + languageChanged */
export function setDayjsLocale(lng: string): void {
  dayjs.locale(lng);
}

export type DateInput = string | number | Date | null | undefined;

interface FormatOptions {
  fallback?: string;
  tz?: string;
  locale?: string;
}

/** Safe dayjs format — returns fallback on null/undefined/invalid */
function safeDayjsFormat(value: DateInput, formatStr: string, opts: FormatOptions = {}): string {
  const { fallback = '', tz, locale } = opts;
  if (value == null || value === '') return fallback;
  try {
    let d = tz ? dayjs(value).tz(tz) : dayjs(value);
    if (!d.isValid()) return fallback;
    if (locale) d = d.locale(locale);
    return d.format(formatStr);
  } catch {
    return fallback;
  }
}

/**
 * Convert a "display-time-as-UTC" value from the backend back to local time.
 * The backend stores the user's intended display time encoded as UTC (e.g. 3:00 PM local
 * is stored as 2026-03-10T15:00:00Z). We need to reverse this to get the actual local Date.
 */
export function fromDisplayUtc(isoString: string): Date {
  const d = new Date(isoString);
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
}

/**
 * #2067 — the time half of a pattern, 12- or 24-hour.
 *
 * `hour12` comes from the profile Time Format via `useTimeFormat`. It is the
 * LAST parameter on every formatter below and defaults to 12-hour, so a call
 * site that has not been converted keeps rendering exactly what it renders
 * today: this file can be adopted one caller at a time without a silent flip.
 *
 * `undefined` means nothing is stored and resolves to 12-hour here. That is
 * deliberately not what the calendar does — `fullCalendarTimeFormatProps`
 * returns `{}` and lets FullCalendar's locale decide — but dayjs renders the
 * pattern it is handed and has no locale-decides mode, so there is no third
 * pattern to pick.
 *
 * React callers should use `useDateFormatters`, which binds `hour12` once.
 * The raw functions stay exported for code with no React context (CSV export,
 * per-feature util modules).
 */
type Hour12 = boolean | undefined;

/** "4/3/26, 10:33 AM" — or "4/3/26, 10:33" on a 24-hour profile. Grid display. */
export function formatDateShort(value: DateInput, fallback = '', hour12?: Hour12): string {
  const pattern = hour12 === false ? 'M/D/YY, HH:mm' : 'M/D/YY, h:mm A';
  return safeDayjsFormat(value, pattern, { fallback });
}

/** "Apr 03, 2026 at 10:33 AM" — or "… at 10:33". */
export function formatDateTimeLong(value: DateInput, fallback = '', hour12?: Hour12): string {
  const pattern = hour12 === false ? 'MMM DD, YYYY [at] HH:mm' : 'MMM DD, YYYY [at] h:mm A';
  return safeDayjsFormat(value, pattern, { fallback });
}

/** "April 3, 2026" — long date, no time */
export function formatDateLong(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMMM D, YYYY', { fallback });
}

/** "Apr 3, 2026" — medium date, no time */
export function formatDateMedium(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMM D, YYYY', { fallback });
}

/** "04/03/2026, 10:33 AM" — or "04/03/2026, 10:33". Compact with time. */
export function formatDateTimeCompact(value: DateInput, fallback = '', hour12?: Hour12): string {
  const pattern = hour12 === false ? 'MM/DD/YYYY, HH:mm' : 'MM/DD/YYYY, hh:mm A';
  return safeDayjsFormat(value, pattern, { fallback });
}

/** "Apr 3, 2026, 10:33:09 AM" — with seconds, optional locale for i18n. */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = '',
  locale?: string,
  hour12?: Hour12,
): string {
  const pattern = hour12 === false ? 'MMM D, YYYY, HH:mm:ss' : 'MMM D, YYYY, hh:mm:ss A';
  return safeDayjsFormat(value, pattern, { fallback, locale });
}

/** "2026-04-03 10:33:09" — machine-readable for CSV export */
export function formatDateForCsv(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'YYYY-MM-DD HH:mm:ss', { fallback });
}

/** Format with any dayjs pattern — for SmallApp manifest format strings.
 *  Pass `tz` (e.g. 'America/Chicago') to convert before formatting.
 *  Pass `locale` (e.g. 'ja') for localized month/day names. */
export function formatWithPattern(
  value: DateInput,
  pattern: string,
  fallback = '',
  opts?: { tz?: string; locale?: string },
): string {
  return safeDayjsFormat(value, pattern, { fallback, ...opts });
}
